/**
 * Generates a code verifier for PKCE (Proof Key for Code Exchange) OAuth2 flow.
 * A code verifier is a high-entropy cryptographic random string using the
 * unreserved characters [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~", with a minimum length of 43 characters and a maximum length of 128 characters.
 *
 * @returns {string} A base64-url-encoded random string to be used as the code verifier.
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generates a code challenge for the PKCE (Proof Key for Code Exchange) OAuth2 flow.
 *
 * The code challenge is created by taking the SHA-256 hash of the code verifier,
 * then encoding it using base64-url encoding, as specified in RFC 7636.
 *
 * @param {string} verifier - The code verifier, a high-entropy cryptographic random string.
 * @returns {Promise<string>} A promise that resolves to the base64-url-encoded SHA-256 hash of the verifier.
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Encodes a Uint8Array into a base64-url-encoded string.
 *
 * Base64-url encoding is similar to standard Base64 encoding, but:
 *   - Replaces '+' with '-'
 *   - Replaces '/' with '_'
 *   - Removes '=' padding characters
 * This ensures the encoded string is safe to use in URLs and as OAuth PKCE values.
 *
 * @param {Uint8Array} array - The binary data to encode.
 * @returns {string} The base64-url-encoded representation of the input data.
 */
export function base64UrlEncode(array: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...array));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
